import type { Composition } from "../../engine/composition";
import { createDetachedExportComposition } from "./DetachedExportCompositionService";
import type { ResolveAssetUrlFn, ResolveEnhancedAudioUrlFn } from "./CompositionSessionService";
import { CompositionService } from "./CompositionService";
import { ScriptLayerStore } from "../store/ScriptLayerStore";

type CaptionsSeedMaps = {
    captionsWordsByClipId?: Record<string, any[]>;
    captionsConfigByClipId?: Record<string, any>;
    textConfigByClipId?: Record<string, any>;
};

export class SceneThumbnailService {
    private static compositionId: string | null = null;
    private static resolveAssetUrl: ResolveAssetUrlFn | null = null;
    private static resolveEnhancedAudioUrl: ResolveEnhancedAudioUrlFn | undefined;

    private static debounceTimer: number | null = null;
    private static inFlight: Promise<void> | null = null;
    private static pending = false;
    private static requestSeq = 0;

    static init(args: {
        compositionId: string;
        resolveAssetUrl: ResolveAssetUrlFn;
        resolveEnhancedAudioUrl?: ResolveEnhancedAudioUrlFn;
    }): void {
        this.compositionId = String(args.compositionId ?? "") || null;
        this.resolveAssetUrl = args.resolveAssetUrl ?? null;
        this.resolveEnhancedAudioUrl = args.resolveEnhancedAudioUrl;
    }

    static teardown(): void {
        this.compositionId = null;
        this.resolveAssetUrl = null;
        this.resolveEnhancedAudioUrl = undefined;
        this.requestSeq++;
        this.pending = false;
        if (this.debounceTimer) {
            window.clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }
    }

    static requestRefresh(opts?: { debounceMs?: number }): void {
        const debounceMs = Number(opts?.debounceMs ?? 350);
        this.requestSeq++;

        if (this.debounceTimer) {
            window.clearTimeout(this.debounceTimer);
            this.debounceTimer = null;
        }

        this.debounceTimer = window.setTimeout(() => {
            this.debounceTimer = null;
            void this.run();
        }, Number.isFinite(debounceMs) && debounceMs >= 0 ? debounceMs : 0);
    }

    private static captureLiveSeeds(): CaptionsSeedMaps {
        const liveComp: any = CompositionService.isInitialized() ? (CompositionService.composition as any) : null;
        if (!liveComp) return {};

        const captionsWordsByClipId: Record<string, any[]> = {};
        const captionsConfigByClipId: Record<string, any> = {};
        const textConfigByClipId: Record<string, any> = {};

        try {
            for (const tr of (liveComp?.tracks ?? []) as any[]) {
                for (const c of (tr?.clips ?? []) as any[]) {
                    if (!c) continue;
                    const type = String(c.type ?? "");
                    const id = String(c.id ?? "");
                    if (!id) continue;

                    if (type === "captions") {
                        try {
                            captionsConfigByClipId[id] = {
                                scriptClipId: c?.scriptClipId ?? null,
                                timingScope: c?.timingScope ?? null,
                                displayMode: c?.displayMode ?? null,
                                textAlign: c?.textAlign ?? null,
                                textBaseline: c?.textBaseline ?? null,
                                textCase: c?.textCase ?? null,
                                maxWidth: c?.maxWidth ?? null,
                                textStyle: c?.textStyle ? JSON.parse(JSON.stringify(c.textStyle)) : null,
                                highlightStyle: c?.highlightStyle ? JSON.parse(JSON.stringify(c.highlightStyle)) : null,
                                background: c?.background ? JSON.parse(JSON.stringify(c.background)) : null,
                                phraseConfig: c?.phraseConfig ? JSON.parse(JSON.stringify(c.phraseConfig)) : null,
                                sceneStyleOverrides: c?.sceneStyleOverrides
                                    ? JSON.parse(JSON.stringify(c.sceneStyleOverrides))
                                    : null,
                                position: c?.position ? JSON.parse(JSON.stringify(c.position)) : null,
                                width: c?.width ?? null,
                                height: c?.height ?? null,
                                scale: c?.scale ? JSON.parse(JSON.stringify(c.scale)) : null,
                                rotation: c?.rotation ?? null,
                                alpha: c?.alpha ?? null,
                            };
                        } catch {}

                        const words = (c as any).words;
                        if (Array.isArray(words) && words.length > 0) {
                            captionsWordsByClipId[id] = words.map((w: any) => ({ ...(w ?? {}) }));
                        }
                    } else if (type === "text") {
                        try {
                            textConfigByClipId[id] = {
                                text: c?.text ?? null,
                                textAlign: c?.textAlign ?? null,
                                textBaseline: c?.textBaseline ?? null,
                                textCase: c?.textCase ?? null,
                                maxWidth: c?.maxWidth ?? null,
                                textStyle: c?.textStyle ? JSON.parse(JSON.stringify(c.textStyle)) : null,
                                sceneStyleOverrides: c?.sceneStyleOverrides
                                    ? JSON.parse(JSON.stringify(c.sceneStyleOverrides))
                                    : null,
                                position: c?.position ? JSON.parse(JSON.stringify(c.position)) : null,
                                width: c?.width ?? null,
                                height: c?.height ?? null,
                                scale: c?.scale ? JSON.parse(JSON.stringify(c.scale)) : null,
                                rotation: c?.rotation ?? null,
                                alpha: c?.alpha ?? null,
                            };
                        } catch {}
                    }
                }
            }
        } catch {}

        const out: CaptionsSeedMaps = {};
        if (Object.keys(captionsWordsByClipId).length > 0) out.captionsWordsByClipId = captionsWordsByClipId;
        if (Object.keys(captionsConfigByClipId).length > 0) out.captionsConfigByClipId = captionsConfigByClipId;
        if (Object.keys(textConfigByClipId).length > 0) out.textConfigByClipId = textConfigByClipId;
        return out;
    }

    private static findDetachedScriptClip(comp: Composition): any | null {
        try {
            const scriptId = (comp as any)?.scriptLayer?.clipId;
            if (scriptId) {
                const found = (comp as any).getClipById?.(scriptId);
                if (found && String((found as any).type) === "script") return found;
            }
        } catch {}

        try {
            for (const tr of (comp as any)?.tracks ?? []) {
                for (const c of (tr as any)?.clips ?? []) {
                    if (c && String((c as any).type) === "script") return c;
                }
            }
        } catch {}

        return null;
    }

    private static pickThumbnailCompTime(args: {
        scriptClip: any;
        sceneSrcStartSec: number;
        sceneSrcEndSec: number;
        offsetSec: number;
    }): number | null {
        const scriptClip = args.scriptClip;
        const srcStartSec = Number(args.sceneSrcStartSec ?? 0);
        const srcEndSec = Number(args.sceneSrcEndSec ?? srcStartSec);
        const offsetSec = Number(args.offsetSec ?? 0);
        if (!Number.isFinite(srcStartSec) || !Number.isFinite(srcEndSec) || srcEndSec <= srcStartSec) return null;

        const segs: any[] = Array.isArray(scriptClip?.scriptSegments) ? scriptClip.scriptSegments : [];
        if (!segs.length) return null;

        const EPS = 1e-3;
        const preferredSrc = Math.min(srcStartSec + Math.max(0, offsetSec), srcEndSec - 0.01);

        // 1) Prefer mapping the desired source time inside the scene (start + offset).
        for (const seg of segs) {
            const s0 = Number((seg as any)?.srcStartSec ?? Number.NaN);
            const s1 = Number((seg as any)?.srcEndSec ?? Number.NaN);
            const c0 = Number((seg as any)?.compStartSec ?? Number.NaN);
            if (!Number.isFinite(s0) || !Number.isFinite(s1) || !Number.isFinite(c0)) continue;
            if (s1 <= s0 + EPS) continue;
            if (preferredSrc >= s0 && preferredSrc < s1 - EPS) {
                return c0 + (preferredSrc - s0);
            }
        }

        // 2) Fallback to the first playable overlap within the scene.
        for (const seg of segs) {
            const s0 = Number((seg as any)?.srcStartSec ?? Number.NaN);
            const s1 = Number((seg as any)?.srcEndSec ?? Number.NaN);
            const c0 = Number((seg as any)?.compStartSec ?? Number.NaN);
            if (!Number.isFinite(s0) || !Number.isFinite(s1) || !Number.isFinite(c0)) continue;
            if (s1 <= s0 + EPS) continue;

            const overlapStart = Math.max(srcStartSec, s0);
            const overlapEnd = Math.min(srcEndSec, s1);
            if (overlapEnd <= overlapStart + 0.01) continue;

            const src = Math.min(overlapStart + Math.max(0, offsetSec), overlapEnd - 0.01);
            return c0 + (src - s0);
        }

        return null;
    }

    private static clampTimeToComposition(comp: Composition, timeSec: number): number {
        const fps = Number((comp as any)?.fps ?? 30) || 30;
        const maxT = Math.max(0, Number((comp as any)?.duration?.seconds ?? 0) - 1 / fps);
        const t = Number.isFinite(timeSec) ? timeSec : 0;
        return Math.max(0, Math.min(t, maxT));
    }

    static async run(): Promise<void> {
        const myReq = this.requestSeq;
        const session = CompositionService.getSessionVersion?.() ?? 0;

        if (!this.compositionId || !this.resolveAssetUrl) return;
        if (ScriptLayerStore.scenes.length === 0) return;

        if (this.inFlight) {
            this.pending = true;
            return;
        }

        this.inFlight = (async () => {
            let detached: Composition | null = null;
            try {
                const seeds = this.captureLiveSeeds();

                detached = await createDetachedExportComposition({
                    compositionId: this.compositionId!,
                    resolveAssetUrl: this.resolveAssetUrl!,
                    resolveEnhancedAudioUrl: this.resolveEnhancedAudioUrl,
                    ...seeds,
                });

                if ((CompositionService.getSessionVersion?.() ?? 0) !== session) return;
                if (this.requestSeq !== myReq) return;

                const scriptClip = this.findDetachedScriptClip(detached);
                if (!scriptClip) return;

                const engineScenes: any[] = typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
                if (!Array.isArray(engineScenes) || engineScenes.length === 0) return;

                const THUMBNAIL_OFFSET_SEC = 0.5;
                const THUMB_WIDTH_PX = 320;
                const quality = 0.75;

                for (const sc of engineScenes) {
                    if ((CompositionService.getSessionVersion?.() ?? 0) !== session) return;
                    if (this.requestSeq !== myReq) return;

                    const index = Number((sc as any)?.index ?? Number.NaN);
                    if (!Number.isFinite(index) || index < 0) continue;

                    const srcStartSec = Number((sc as any)?.startSec ?? 0);
                    const srcEndSec = Number((sc as any)?.endSec ?? srcStartSec);
                    if (!Number.isFinite(srcStartSec) || !Number.isFinite(srcEndSec) || srcEndSec <= srcStartSec) continue;

                    const compTime =
                        this.pickThumbnailCompTime({
                            scriptClip,
                            sceneSrcStartSec: srcStartSec,
                            sceneSrcEndSec: srcEndSec,
                            offsetSec: THUMBNAIL_OFFSET_SEC,
                        }) ?? (srcStartSec + THUMBNAIL_OFFSET_SEC);

                    const t = this.clampTimeToComposition(detached, compTime);
                    const url = await detached.screenshotAt(t, "jpeg", quality, {
                        maxWidthPx: THUMB_WIDTH_PX,
                        restore: false,
                    });

                    if ((CompositionService.getSessionVersion?.() ?? 0) !== session) return;
                    if (this.requestSeq !== myReq) return;

                    if (url) {
                        ScriptLayerStore.setSceneThumbnail(`scene_${index}`, url);
                    }
                }
            } catch (err) {
                console.warn("[SceneThumbnailService] Thumbnail generation failed:", err);
            } finally {
                try {
                    await detached?.dispose?.();
                } catch {}
            }
        })();

        try {
            await this.inFlight;
        } finally {
            this.inFlight = null;
        }

        if (this.pending) {
            this.pending = false;
            // Run again immediately to incorporate the latest request, but avoid unbounded loops.
            if (this.requestSeq !== myReq) this.requestRefresh({ debounceMs: 0 });
        }
    }
}
